import httpService from '../../services/http.service';
import {
	mockCheckId,
	mockSendOtp,
	mockVerifyOtp,
	mockVerifyPassword,
	USE_MOCK_SIGN_IN_V2,
} from './SignInV2.mock';

export interface CheckIdResult {
	valid: boolean;
	requires2fa: boolean;
	hasPhone: boolean;
	hasMail: boolean;
	phone?: string;
	mail?: string;
	stepToken?: string;
}

/** where the OTP was sent - phone first, mail as fallback */
export type OtpChannelType = 'phone' | 'mail';

export interface VerifyPasswordResult {
	ok: boolean;
	sentTo?: OtpChannelType;
	stepToken?: string;
}

export interface SendOtpResult {
	sentTo?: OtpChannelType;
	stepToken?: string;
}

export interface VerifyOtpResult {
	ok: boolean;
	/**
	 * The credentials payload to hand to `loginStep2SuccessCallback`. Only the
	 * real endpoint can produce one, so it is absent while mocked - the caller
	 * must treat "ok without a payload" as "mock mode, nothing to log in with".
	 */
	loginPayload?: any;
}

/** the server answers {ok, sentTo, stepToken} for both otp-sending endpoints */
interface OtpSentResponse {
	ok: boolean;
	sentTo?: OtpChannelType;
	stepToken?: string;
}

/** matches the 30s the existing login calls use - these hit the Liram api */
const LOGIN_TIMEOUT = 30000;

/**
 * Validates the teudat zehut against the server and returns whether any of the
 * employee's employers requires 2FA + which OTP destinations exist. The phone
 * and mail come back already masked - they are for display only.
 */
export const checkId = (teudatZehut: string): Promise<CheckIdResult> =>
	USE_MOCK_SIGN_IN_V2
		? mockCheckId(teudatZehut)
		: httpService.api<CheckIdResult>({
				type: 'loginCheckId',
				data: { login: teudatZehut },
				timeout: LOGIN_TIMEOUT,
			});

/**
 * 2FA branch only: verifies the password WITHOUT logging in, then triggers the
 * OTP send and reports where it went. Employees whose employers don't require
 * 2FA go through the existing `login` redux thunk instead.
 * A wrong password is answered with 200 {ok:false}, not an error status.
 */
export const verifyPassword = async (
	teudatZehut: string,
	password: string,
	stepToken?: string,
): Promise<VerifyPasswordResult> => {
	if (USE_MOCK_SIGN_IN_V2) {
		return mockVerifyPassword(teudatZehut, password);
	}
	const res = await httpService.api<OtpSentResponse>({
		type: 'loginPassword',
		data: { login: teudatZehut, password, stepToken },
		timeout: LOGIN_TIMEOUT,
	});
	return { ok: !!res?.ok, sentTo: res?.sentTo, stepToken: res?.stepToken };
};

/**
 * Sends (or resends) the one-time code and reports where it landed. Used by the
 * one-time-code alternative and by resend on the OTP step - the 2FA branch gets
 * its first code from verifyPassword instead.
 */
export const sendOtp = async (
	teudatZehut: string,
	stepToken?: string,
): Promise<SendOtpResult> => {
	if (USE_MOCK_SIGN_IN_V2) {
		return mockSendOtp(teudatZehut);
	}
	const res = await httpService.api<OtpSentResponse>({
		type: 'loginSendOtp',
		data: { login: teudatZehut, stepToken },
		timeout: LOGIN_TIMEOUT,
	});
	return { sentTo: res?.sentTo, stepToken: res?.stepToken };
};

/**
 * Verifies the code through the existing login-step2 endpoint, which is also
 * the final step of the 2FA flow. Its response is the credentials payload, so
 * it becomes `loginPayload`. A wrong code is a 400 - that is the "ok:false"
 * case rather than a failure to report.
 */
export const verifyOtp = async (
	teudatZehut: string,
	code: string,
	stepToken?: string,
): Promise<VerifyOtpResult> => {
	if (USE_MOCK_SIGN_IN_V2) {
		return mockVerifyOtp(teudatZehut, code);
	}
	try {
		const res = await httpService.api<any>({
			type: 'loginWithCode2',
			data: { login: teudatZehut, password: code, stepToken },
			timeout: LOGIN_TIMEOUT,
		});
		return { ok: true, loginPayload: res };
	} catch (err: any) {
		if (err?.status === 400) {
			return { ok: false };
		}
		throw err;
	}
};

/**
 * httpService rejects with the axios response (which has a numeric status) when
 * the server answered, and with undefined when the request never reached it.
 */
export const isConnectionError = (err: any) =>
	!err || typeof err.status !== 'number';
